import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { apiClientV2 } from '~/api/client';
import { logger } from '~/core/logger/logger';
import { CREATOR_PROFILE_QUERY_KEY, MEDIA_KIT } from '~/queries/keys';

export interface UpdateCreatorProfilePayload {
  firstName?: string;
  lastName?: string | null;
  biography?: string | null;
  location?: string | null; // JSON stringified location object
  categoryAgency?: string | null; // comma-separated string
  genderAgency?: string | null;
  emailContact?: string;
  dateOfBirth?: string | null; // YYYY-MM-DD format
  avatar?: File | Blob; // Avatar file
  webLinks?: string | null; // JSON stringified webLinks array
}

export type UpdateCreatorProfileResponse = null; // 204 No Content

export const useUpdateCreatorProfile = () => {
  const queryClient = useQueryClient();

  return useMutation<
    UpdateCreatorProfileResponse,
    AxiosError,
    UpdateCreatorProfilePayload
  >({
    mutationFn: (payload) => {
      const formData = new FormData();

      // Add each field to FormData - include all fields including empty strings
      if (payload.firstName !== undefined)
        formData.append('firstName', payload.firstName);
      if (payload.lastName !== undefined)
        formData.append('lastName', payload.lastName || '');
      if (payload.biography !== undefined)
        formData.append('biography', payload.biography || '');
      if (payload.location !== undefined)
        formData.append('location', payload.location || '');
      if (payload.categoryAgency !== undefined)
        formData.append('categoryAgency', payload.categoryAgency || '');
      if (payload.genderAgency !== undefined)
        formData.append('genderAgency', payload.genderAgency || '');
      if (payload.emailContact !== undefined)
        formData.append('emailContact', payload.emailContact);
      if (payload.dateOfBirth !== undefined)
        formData.append('dateOfBirth', payload.dateOfBirth || '');
      if (payload.avatar) formData.append('avatar', payload.avatar);
      if (payload.webLinks !== undefined)
        formData.append('webLinks', payload.webLinks || '');

      return apiClientV2
        .patch('/creators/profile', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        .then((response) => response.data);
    },

    onSuccess: () => {
      // Invalidate creator profile query
      queryClient.invalidateQueries({
        queryKey: [CREATOR_PROFILE_QUERY_KEY],
      });

      queryClient.invalidateQueries({
        queryKey: [MEDIA_KIT],
        exact: false,
      });
    },

    onError: (error: AxiosError) => {
      logger
        .withMetadata({
          error: error,
          section: 'profile-settings',
        })
        .error('Creator Profile Settings Update Error');
    },
  });
};
